import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Section } from "@/components/app/Primitives";
import { Button } from "@/components/ui/button";
import { SimBadge } from "@/components/engage/ResultBadge";
import { Download, PlayCircle, AlertTriangle, KeyRound, ExternalLink, Copy } from "lucide-react";

const HOME = { ey_auditor: "/ey", procurement_desk: "/procurement", customer: "/customer", propay_admin: "/admin", hul_approver: "/approver", influencer: "/influencer" };

export default function UsageScenarios() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("all");
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/usage-scenarios").then((r) => { setData(r.data); setOpen(r.data.scenarios[0]?.id); }); }, []);
  const home = HOME[user?.role] || "/admin";
  const pdf = async () => {
    const resp = await fetch(`${BACKEND_URL}/api/usage-scenarios.pdf`, { credentials: "include" });
    if (!resp.ok) return toast.error("PDF failed");
    const url = URL.createObjectURL(await resp.blob());
    Object.assign(document.createElement("a"), { href: url, download: "Engage360-Application-Usage-Scenarios.pdf" }).click();
    URL.revokeObjectURL(url);
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  if (!data) return <div className="text-sm text-slate-400">Loading scenarios…</div>;
  const list = data.scenarios.filter((s) => kind === "all" || s.kind === kind);
  return (
    <div className="space-y-6" data-testid="usage-scenarios-page">
      <Section title="Application Usage Scenarios" subtitle={`Step-by-step demo scripts for every role — happy flow and unhappy flows — with the login to use at each step. Version ${data.version}.`}
        right={<Button onClick={pdf} className="bg-[#0052FF] hover:bg-[#0043d1]" data-testid="usage-pdf-btn"><Download size={15} className="mr-1" /> Download PDF</Button>} />

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="usage-logins">
        <div className="flex items-center gap-2 font-heading font-bold text-slate-900 mb-3"><KeyRound size={16} className="text-[#0052FF]" /> Demo logins <SimBadge label="DEMO ACCOUNTS" /></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(data.users).map(([k, u]) => (
            <div key={k} className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs" data-testid={`usage-login-${k}`}>
              <div className="font-semibold text-slate-800">{u.role}</div>
              <button onClick={() => copy(u.email)} className="mt-1 flex items-center gap-1 font-mono text-slate-700 hover:text-[#0052FF]">{u.email} <Copy size={11} /></button>
              <div className="font-mono text-slate-500 mt-0.5">{u.password}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-1 text-xs">{[["all", "All"], ["happy", "Happy flow"], ["unhappy", "Unhappy flows"]].map(([k, l]) => <button key={k} onClick={() => setKind(k)} data-testid={`usage-filter-${k}`} className={`rounded-full px-3 py-1 font-semibold ${kind === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>)}</div>

      <div className="space-y-3">
        {list.map((sc, n) => (
          <div key={sc.id} className={`rounded-xl border bg-white overflow-hidden ${sc.kind === "happy" ? "border-emerald-200" : "border-slate-200"}`} data-testid={`usage-scenario-${sc.id}`}>
            <button onClick={() => setOpen(open === sc.id ? null : sc.id)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50/70">
              <div className="flex items-center gap-3">
                {sc.kind === "happy" ? <PlayCircle className="text-emerald-600" size={18} /> : <AlertTriangle className="text-amber-600" size={18} />}
                <div><div className="font-heading font-bold text-slate-900">{sc.title}</div><div className="text-xs text-slate-500">{sc.goal}</div></div>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500"><span>{sc.steps.length} steps</span>{sc.campaign_id && <Link to={`${home}/campaigns/${sc.campaign_id}`} onClick={(e) => e.stopPropagation()} className="text-[#0052FF] hover:underline flex items-center gap-1" data-testid={`usage-open-${sc.id}`}>open campaign <ExternalLink size={11} /></Link>}</div>
            </button>
            {open === sc.id && (
              <ol className="divide-y divide-slate-100 border-t border-slate-100">
                {sc.steps.map((st, i) => (
                  <li key={i} className="grid md:grid-cols-12 gap-3 px-5 py-3 text-sm" data-testid={`usage-step-${sc.id}-${i + 1}`}>
                    <div className="md:col-span-1 font-mono text-slate-400">{String(i + 1).padStart(2, "0")}</div>
                    <div className="md:col-span-3"><div className="font-semibold text-slate-800">{st.role}</div><button onClick={() => copy(st.email)} className="font-mono text-[11px] text-slate-500 hover:text-[#0052FF]">{st.email}</button><div className="font-mono text-[11px] text-slate-400">{st.user_key === "creator" ? "OTP on screen" : st.password}</div></div>
                    <div className="md:col-span-3 text-slate-700">{st.action}</div>
                    <div className="md:col-span-2 text-[11px] font-mono text-slate-500 break-all">{st.where === "campaign" ? (sc.campaign_id ? <Link className="text-[#0052FF] hover:underline" to={`${home}/campaigns/${sc.campaign_id}${st.tab ? `?tab=${st.tab}` : ""}`}>tab: {st.tab}</Link> : `tab: ${st.tab}`) : <Link className="text-[#0052FF] hover:underline" to={st.where.startsWith("/") ? st.where : home}>{st.where}</Link>}</div>
                    <div className="md:col-span-3 text-xs text-slate-600"><span className="text-emerald-700 font-semibold">Expect:</span> {st.expected}</div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
